import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sun, ShieldCheck, Loader2, CheckCircle2, Download, Mail, Phone } from "lucide-react";
import { brandDocument } from "@/lib/publicHost";

const money = (v) => `$${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const NARR = [
  ["executive_summary", "Executive summary"],
  ["project_understanding", "Project understanding"],
  ["execution_strategy", "Execution strategy"],
  ["schedule_narrative", "Schedule"],
  ["staffing_narrative", "Staffing"],
  ["safety_quality", "Safety & quality"],
  ["bid_confidence", "Bid confidence"],
  ["closing", "Closing"],
];

// Expired / revoked / unknown link: a Power Ray page that says who to contact. Never a GridForge error page.
export function PublicUnavailable({ code = "not_found" }) {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/public/branding").then((r) => { setB(r.data); brandDocument(r.data.company_name, r.data.logo); }).catch(() => setB({})); }, []);
  const company = b?.company_name || "Power Ray LLC";
  const c = b?.contact || {};
  const primary = b?.primary || "#003E87";
  const headline = code === "expired" ? "This proposal link has expired." : code === "revoked" ? "This proposal link is no longer active." : "This page is not available.";
  return (
    <div className="min-h-screen bg-slate-100 grid place-items-center px-4" data-testid="public-proposal-unavailable">
      <div className="max-w-md w-full bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-8 py-5 text-white flex items-center gap-3" style={{ background: primary }}>
          {b?.logo ? <img src={b.logo} alt="" className="h-8 w-auto" /> : <Sun className="w-7 h-7" style={{ color: b?.accent || "#F4C400" }} />}
          <div className="font-bold">{company}</div>
        </div>
        <div className="px-8 py-7">
          <h1 className="text-lg font-semibold text-slate-900" data-testid="public-unavailable-headline">{headline}</h1>
          <p className="text-sm text-slate-600 mt-2" data-testid="public-unavailable-contact">For the current proposal, please contact {c.name || "Taylor"}{c.title ? `, ${c.title}` : ""} at {company}.</p>
          <div className="mt-4 space-y-1.5 text-sm">
            {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 underline" style={{ color: primary }} data-testid="public-contact-email"><Mail className="w-4 h-4" /> {c.email}</a>}
            {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-2 underline" style={{ color: primary }} data-testid="public-contact-phone"><Phone className="w-4 h-4" /> {c.phone}</a>}
          </div>
        </div>
      </div>
    </div>
  );
}

// Uploaded or filed proposal: the client sees the PDF itself, plus the stated price when given.
function ProvidedBody({ d, token, primary }) {
  const src = `${API_BASE}/public/proposal/${token}/pdf`;
  const p = d.provided || {};
  return (
    <>
      {p.total ? (
        <div className="mt-5 rounded-xl border border-slate-200 p-5 flex items-end justify-between" data-testid="public-proposal-price">
          <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Proposal price</div>
            <div className="text-3xl font-bold" style={{ color: primary }}>{money(p.total)}</div></div>
          {p.cost_per_watt ? <div className="text-sm text-slate-400 font-mono">${Number(p.cost_per_watt).toFixed(4)}/W</div> : null}
        </div>
      ) : null}
      {d.pdf_available && <iframe title="Proposal" src={src} data-testid="public-proposal-pdf" className="mt-5 w-full h-[80vh] rounded-lg border border-slate-200 bg-slate-50" />}
    </>
  );
}

// Public, tokenized proposal view (no login): logs the open, how long it stayed open and how far it was read; captures an e-signature.
export default function PublicProposal() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [status, setStatus] = useState("loading");
  const [errCode, setErrCode] = useState("not_found");
  const seen = useRef(new Set());
  const t0 = useRef(Date.now());

  useEffect(() => {
    const html = document.documentElement;
    const wasDark = html.classList.contains("dark");
    html.classList.remove("dark");
    return () => { if (wasDark) html.classList.add("dark"); };
  }, []);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [email, setEmail] = useState("");
  const [signing, setSigning] = useState(false);
  const [sig, setSig] = useState(null);

  useEffect(() => {
    api.get(`/public/proposal/${token}`)
      .then((r) => { setD(r.data); setSig(r.data.signature || null); setStatus("ok"); brandDocument(r.data.branding?.company_name, r.data.branding?.logo); })
      .catch((e) => { setErrCode(e.response?.data?.detail?.code || "not_found"); setStatus("error"); });
  }, [token]);

  // heartbeat: seconds open + sections read (sent every 15 s and on leave)
  useEffect(() => {
    if (!d?.view_id) return undefined;
    const send = (beacon) => {
      const body = JSON.stringify({ view_id: d.view_id, seconds: Math.round((Date.now() - t0.current) / 1000), pages: seen.current.size });
      const url = `${API_BASE}/public/proposal/${token}/ping`;
      if (beacon && navigator.sendBeacon) navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      else api.post(`/public/proposal/${token}/ping`, JSON.parse(body)).catch(() => {});
    };
    const iv = setInterval(() => send(false), 15000);
    const leave = () => send(true);
    window.addEventListener("pagehide", leave);
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") leave(); });
    const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) seen.current.add(e.target.dataset.sec); }), { threshold: 0.4 });
    document.querySelectorAll("[data-sec]").forEach((el) => io.observe(el));
    return () => { clearInterval(iv); window.removeEventListener("pagehide", leave); io.disconnect(); };
  }, [d, token]);

  if (status === "loading") return <div className="min-h-screen grid place-items-center bg-slate-100"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (status === "error" || !d) return <PublicUnavailable code={errCode} />;

  const b = d.branding || {};
  const doc = d.doc || {};
  const s = doc.sections || {};
  const totals = doc.totals || {};
  const primary = b.primary || "#003E87";
  const accent = b.accent || "#F4C400";
  const company = b.company_name || "Power Ray LLC";
  const perW = totals.cost_per_watt ? `$${Number(totals.cost_per_watt).toFixed(4)}/W` : "";
  const execPhases = Array.isArray(s.execution_plan) ? s.execution_plan : [];
  const pdfSrc = `${API_BASE}/public/proposal/${token}/pdf`;

  const sign = async () => {
    if (!name.trim()) { toast.error("Please type your full name to sign."); return; }
    setSigning(true);
    try {
      const r = await api.post(`/public/proposal/${token}/sign`, { name: name.trim(), title: title.trim(), email: email.trim() });
      setSig(r.data.signature); toast.success("Thank you — your acceptance is recorded.");
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Could not record signature"); }
    setSigning(false);
  };

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="public-proposal">
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-8 py-6 text-white flex items-center justify-between gap-4" style={{ background: primary }}>
          <div className="flex items-center gap-3 min-w-0">
            {b.logo ? <img src={b.logo} alt="" className="h-9 w-auto shrink-0" /> : <Sun className="w-8 h-8 shrink-0" style={{ color: accent }} />}
            <div className="min-w-0"><div className="font-bold text-lg leading-tight truncate" data-testid="public-company">{company}</div>
              <div className="text-xs opacity-80">{d.provided?.label || <>Proposal &amp; Execution Plan</>}</div></div>
          </div>
          <div className="text-right text-xs opacity-90 shrink-0"><div>Prepared for</div><div className="font-semibold text-sm">{d.client_name || "—"}</div></div>
        </div>

        <div className="px-8 py-7">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{d.name}</h1>
              <div className="text-sm text-slate-500 mt-1">{d.capacity_mw ? `${d.capacity_mw} MWdc` : ""}</div>
            </div>
            {d.pdf_available && (
              <a href={pdfSrc} target="_blank" rel="noreferrer" data-testid="public-proposal-pdf-open"
                className="inline-flex items-center gap-1.5 text-sm font-medium rounded-md px-3 py-1.5 text-white" style={{ background: primary }}>
                <Download className="w-4 h-4" /> Download the PDF
              </a>
            )}
          </div>

          {d.provided ? <ProvidedBody d={d} token={token} primary={primary} /> : (<>
          <div className="mt-5 rounded-xl border border-slate-200 p-5 flex items-end justify-between" data-testid="public-proposal-price" data-sec="price">
            <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Firm fixed price</div>
              <div className="text-3xl font-bold" style={{ color: primary }}>{money(totals.total)}</div></div>
            {perW && <div className="text-sm text-slate-400 font-mono">{perW}</div>}
          </div>

          {NARR.map(([k, label]) => (typeof s[k] === "string" && s[k].trim()) ? (
            <section key={k} className="mt-6" data-sec={k}>
              <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: primary }}>{label}</h2>
              <p className="text-sm text-slate-700 leading-relaxed mt-1.5 whitespace-pre-line">{s[k]}</p>
            </section>
          ) : null)}

          {execPhases.length > 0 && (
            <section className="mt-6" data-sec="execution_plan">
              <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: primary }}>Execution plan</h2>
              <div className="mt-2 space-y-2">
                {execPhases.map((p, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 p-3">
                    <div className="text-sm font-semibold text-slate-800">{p.phase}</div>
                    <div className="text-xs text-slate-600 mt-0.5 whitespace-pre-line">{p.narrative}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {(doc.inclusions?.length > 0 || doc.exclusions?.length > 0) && (
            <div className="mt-6 grid sm:grid-cols-2 gap-4" data-sec="scope">
              {doc.inclusions?.length > 0 && (<div><h3 className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1.5">Included</h3>
                <ul className="text-xs text-slate-700 space-y-1 list-disc pl-4">{doc.inclusions.map((x, i) => <li key={i}>{x}</li>)}</ul></div>)}
              {doc.exclusions?.length > 0 && (<div><h3 className="text-xs font-semibold uppercase tracking-wide text-rose-700 mb-1.5">Excluded</h3>
                <ul className="text-xs text-slate-700 space-y-1 list-disc pl-4">{doc.exclusions.map((x, i) => <li key={i}>{x}</li>)}</ul></div>)}
            </div>
          )}
          </>)}

          <div className="mt-8 rounded-xl border-2 p-5" style={{ borderColor: sig ? "#A7F3D0" : accent }} data-testid="public-esign" data-sec="sign">
            {sig ? (
              <div className="flex items-center gap-3" data-testid="public-signed">
                <CheckCircle2 className="w-7 h-7 text-emerald-600 shrink-0" />
                <div><div className="font-semibold text-slate-800">Proposal accepted</div>
                  <div className="text-sm text-slate-600">Signed by <b>{sig.name}</b>{sig.title ? `, ${sig.title}` : ""} on {new Date(sig.signed_at).toLocaleString()}</div></div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-3"><ShieldCheck className="w-5 h-5" style={{ color: primary }} /><h3 className="font-semibold text-slate-800">Accept &amp; e-sign</h3></div>
                <p className="text-xs text-slate-500 mb-3">Typing your name below constitutes your electronic signature accepting this proposal. Your name, title, time and IP address are recorded.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} data-testid="public-sign-name" />
                  <Input placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="public-sign-title" />
                  <Input type="email" placeholder="Work email (at your company)" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="public-sign-email" />
                </div>
                <Button onClick={sign} disabled={signing} className="mt-3 text-black font-semibold hover:brightness-95" style={{ background: accent }} data-testid="public-sign-submit">
                  {signing ? "Recording…" : "Accept & sign"}
                </Button>
              </>
            )}
          </div>

          <div className="mt-6 text-center text-[11px] text-slate-400" data-testid="public-footer">
            {company}{d.contact?.name ? ` · ${d.contact.name}` : ""}{d.contact?.email ? ` · ${d.contact.email}` : ""}{d.contact?.phone ? ` · ${d.contact.phone}` : ""}
            {d.expires_at ? ` · This link is valid through ${new Date(d.expires_at).toLocaleDateString()}` : ""}
          </div>
        </div>
      </div>
    </div>
  );
}
